import { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { fetchFilteredInventory, fetchFilterInitialValues, saveFilter, updateFilter, fetchIssuers, fetchBrokerDealers, getSecurityChargeOptions } from "../../http/api";
import { useLocation } from "react-router-dom";
import { convertMaturityToYears } from "../../Utils/filterUtils";
import { useFilterStore } from "../../Store/filterStore";
import { useRef } from "react";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { clearCharacteristicLinkage } from "../../Utils/viewInventoryHandler";

// Utility debounce function
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export function useFilterPopup(token, navigate, IsEditOn = false) {
  const [tabValue, setTabValue] = useState(0);
  const [criteriaCount, setCriteriaCount] = useState(0);
  const [isCriteriaLoading, setIsCriteriaLoading] = useState(false);
  const [type, setType] = useState("");
  const location = useLocation(); // Add this to access navigation state
  const [isEdit, setIsEdit] = useState(() => {
    // Use the tab from location.state if available, otherwise fall back to prop
    return location.state?.IsEditOn !== undefined ? location.state.IsEditOn : IsEditOn;
  }); 
  const [state, setState] = useState("");
  const [maturityDates, setMaturityDates] = useState({
    Gov: { min: null, max: null },
    Corp: { min: null, max: null },
    Muni: { min: null, max: null }
  });
  const [side, setSide] = useState({
    Gov: "",
    Corp: "", 
    Muni: ""
  });
  const [callable, setCallable] = useState({
    Gov: "",
    Corp: "",
    Muni: "",
  });
  const [yieldToWorst, setYieldToWorst] = useState({
    Gov: { min: null, max: null, check: false },
    Corp: { min: null, max: null, check: false },
    Muni: { min: null, max: null, check: false },
  });
  const [quantity, setQuantity] = useState({
    Gov: { min: null, max: null, check: false },
    Corp: { min: null, max: null, check: false },
    Muni: { min: null, max: null, check: false },
  });
  const [price, setPrice] = useState({
    Gov: { min: null, max: null, check: false },
    Corp: { min: null, max: null, check: false },
    Muni: { min: null, max: null, check: false },
  });
  const [coupon, setCoupon] = useState({
    Gov: { min: null, max: null, check: false },
    Corp: { min: null, max: null, check: false },
    Muni: { min: null, max: null, check: false },
  });
  const [moodyRatings, setMoodyRatings] = useState({
    Corp: { operator: "", value: "" },
    Muni: { operator: "", value: "" },
  });
  const [spRatings, setSpRatings] = useState({
    Corp: { operator: "", value: "" },
    Muni: { operator: "", value: "" },
  });
  const [subTypeOptions, setSubTypeOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [moodyRatingOptions, setMoodyRatingOptions] = useState([]);
  const [spRatingOptions, setSpRatingOptions] = useState([]);
  const [paymentFrequency, setPaymentFrequency] = useState("");
  const [sector, setSector] = useState("");
  const [nextCallDate, setNextCallDate] = useState(null);
  const [taxable, setTaxable] = useState("");
  const [incomeSource, setIncomeSource] = useState("");
  const [altMinTax, setAltMinTax] = useState("");
  const [paymentFrequencyOptions] = useState(["Monthly", "Quarterly", "Semi-Annual", "Annual"]);
  const [sectorOptions] = useState(
    ["Technology",
    "Materials",
    "Industrial",
    "Financial",
    "Real Estate",
    "Healthcare",
    "Consumer Discretionary",
    "Utility",
    "Communication Services",
    "Consumer Staples",
    "Energy"]
  );
  const [incomeSourceOptions] = useState(["General Obligation", "Revenue", "Double Barreled"]);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [filterName, setFilterName] = useState("");
  //here declare useeffect for isEdit and setEdit from the params as default
  const [editFilterName, setEditFilterName] = useState("");
  const [savedFiltersId, setSavedFiltersId] = useState(null);
  const [isLoadingFilter, setIsLoadingFilter] = useState(false); // New flag to track filter loading
  const [tabSwitchingEnabled, setTabSwitchingEnabled] = useState(false); // New state for tab switching
  const [preserveFilterData, setPreserveFilterData] = useState(false); // New flag to preserve filter data
  const tabEnum = { 0: "Gov", 1: "Corp", 2: "Muni" };
  const currentTab = tabEnum[tabValue];
  const [issuer, setIssuer] = useState({
    Gov: "",
    Corp: "",
    Muni: ""
  });
  const [description, setDescription] = useState({
    Gov: "",
    Corp: "",
    Muni: ""
  });
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [issuerValidationError, setIssuerValidationError] = useState({
    Gov: false,
    Corp: false,
    Muni: false
  });
  const [brokerDealer, setBrokerDealer] = useState({
    Gov: "",
    Corp: "",
    Muni: "",
  });
  const [brokerDealerOptions, setBrokerDealerOptions] = useState([]);
  const [securityCharge, setSecurityCharge] = useState({
    Gov: [],
    Corp: [],
    Muni: [],
  });
  const [securityChargeOptions, setSecurityChargeOptions] = useState([]);
  // Track source and absolute preview dates for yieldtable flows
  const [source, setSource] = useState("");
  const [maturityPreviewDates, setMaturityPreviewDates] = useState({ from: null, to: null });

  // NEW: preview ranges for ratings (Moody and S&P) when source is yieldtable
  const [ratingPreview, setRatingPreview] = useState({
    Gov: { moody: { from: "", to: "" }, sp: { from: "", to: "" } },
    Corp: { moody: { from: "", to: "" }, sp: { from: "", to: "" } },
    Muni: { moody: { from: "", to: "" }, sp: { from: "", to: "" } },
  });

  // Add: read maturity-edited flag from store
  const maturityEditedByUser = useFilterStore((s) => s.maturityEditedByUser);
  const resetMaturityEditedByUser = useFilterStore((s) => s.resetMaturityEditedByUser);

  // Validate issuer input - update to check only the current tab's issuer
  useEffect(() => {
    const currentIssuer = issuer[currentTab];
    if (currentIssuer && currentIssuer.trim() !== "" && currentTab === "Corp") {
      const hasMatch = issuerOptions.some(option => 
        option.toLowerCase() === currentIssuer.toLowerCase()
      );
      setIssuerValidationError(prev => ({
        ...prev,
        [currentTab]: !hasMatch
      }));
    } else {
      setIssuerValidationError(prev => ({
        ...prev,
        [currentTab]: false
      }));
    }
  }, [issuer, issuerOptions, currentTab]);

  const getMinMaxLabels = (ratingObj, ratingScale, ratingType) => {
    if (!ratingObj.operator || !ratingObj.value) return {};
    const selectedRating = ratingScale.find((r) => r.value === ratingObj.value);
    if (!selectedRating) return {};
    const minKey = `minRating${ratingType}`;
    const maxKey = `maxRating${ratingType}`;
    if (ratingObj.operator === "gt") {
      return { [minKey]: selectedRating.rating, [maxKey]: ratingScale[0].rating };
    }
    if (ratingObj.operator === "lt") {
      return {
        [minKey]: ratingScale[ratingScale.length - 1].rating,
        [maxKey]: selectedRating.rating,
      };
    }
    return {};
  };

  // Fetch initial values when tab changes
  // Fetch initial values when tab changes, but skip if loading a saved filter
  useEffect(() => {
    if (isLoadingFilter) {
      return; // Skip if loading a saved filter
    }

    const payload = { type: currentTab };
    const getFilterInitialValues = async () => {
      const res = await fetchFilterInitialValues(token, payload);
      if (res) {
        // Always set options, even in edit mode
        setSubTypeOptions((res.subType || []).filter(option => option && option.trim() !== ""));
        setStateOptions((res.state || []).filter(option => 
          option && 
          option.value && 
          option.value.trim() !== "" && 
          option.name && 
          option.name.trim() !== ""
        ));
        setSpRatingOptions((res.spRatingMap || []).filter(option => 
          option && 
          option.rating && 
          option.rating.trim() !== "" && 
          option.value !== null && 
          option.value !== undefined
        ));
        setMoodyRatingOptions((res.moodyRatingMap || []).filter(option => 
          option && 
          option.rating && 
          option.rating.trim() !== "" && 
          option.value !== null && 
          option.value !== undefined
        ));
        // Only set default min-max values if NOT in edit mode/loading/preserving
        if (!isEdit && !isLoadingFilter && !preserveFilterData) {
          setYieldToWorst((prev) => ({
            ...prev,
            [currentTab]: {
              min: res.minYtw ?? null,
              max: res.maxYtw ?? null,
              check: false,
            },
          }));
          setQuantity((prev) => ({
            ...prev,
            [currentTab]: {
              min: res.minQty ?? null,
              max: res.maxQty ?? null,
              check: false,
            },
          }));
          setPrice((prev) => ({
            ...prev,
            [currentTab]: {
              min: res.minPrice ?? null,
              max: res.maxPrice ?? null,
              check: false,
            },
          }));
          setCoupon((prev) => ({
            ...prev,
            [currentTab]: {
              min: res.minCoupon ?? null,
              max: res.maxCoupon ?? null,
              check: false,
            },
          }));
        }
      }
    };
    getFilterInitialValues();
  }, [currentTab, isEdit, isLoadingFilter, preserveFilterData, token]);

  // Fetch issuers when component mounts
  useEffect(() => {
    const fetchIssuerOptions = async () => {
      try {
        const issuers = await fetchIssuers(token);
        // Filter out null/empty issuers
        setIssuerOptions((issuers || []).filter(issuer => issuer && issuer.trim() !== ""));
      } catch (error) {
        console.error("Error fetching issuers:", error);
        setIssuerOptions([]);
      }
    };

    fetchIssuerOptions();
  }, [token]);

  // Fetch broker dealers when component mounts
  useEffect(() => {
    const fetchBrokerDealerOptions = async () => {
      try {
        const brokerDealers = await fetchBrokerDealers(token);
        // Filter out null/empty broker dealers
        setBrokerDealerOptions((brokerDealers || []).filter(broker => broker && broker.trim() !== ""));
      } catch (error) {
        console.error("Error fetching broker dealers:", error);
        setBrokerDealerOptions([]);
      }
    };

    fetchBrokerDealerOptions();
  }, [token]);

  // Fetch security charge options when component mounts
  useEffect(() => {
    const fetchSecurityChargeOptions = async () => {
      try {
        const charges = await getSecurityChargeOptions(token);
        setSecurityChargeOptions((charges || []).filter(charge => charge !== null && charge !== undefined && String(charge).trim() !== ""));
      } catch (error) {
        console.error("Error fetching security charges:", error);
        setSecurityChargeOptions([]);
      }
    };

    fetchSecurityChargeOptions();
  }, [token]);

  // Update criteria count when filter criteria change
  useEffect(() => {
    const input = constructFilterInput(true); // Pass true to include pagination for criteria count
    const fetchCriteriaCount = async () => {
      try {
        setIsCriteriaLoading(true);
        const res = await fetchFilteredInventory(token, input);
        if (res && res.totalElements !== undefined) {
          setCriteriaCount(res.totalElements);
        } else {
          setCriteriaCount(0); // Fallback to 0 if response is invalid
          console.warn("Invalid response from fetchFilteredInventory:", res);
        }
      } catch (error) {
        console.error("Error fetching criteria count:", error);
        setCriteriaCount(0); // Set to 0 on error to avoid breaking UI
      } finally {
        setIsCriteriaLoading(false);
      }
    };
    fetchCriteriaCount();
  }, [
    tabValue,
    type,
    state,
    maturityDates,
    side,
    callable,
    paymentFrequency,
    sector,
    nextCallDate,
    taxable,
    incomeSource,
    altMinTax,
    token,
    description, // Add description to dependencies
    issuer, // Add issuer to dependencies
    brokerDealer, // Add brokerDealer to dependencies
    securityCharge, // Add security charge to dependencies
    // Include the complex objects with their nested properties
    JSON.stringify(moodyRatings),
    JSON.stringify(spRatings),
    JSON.stringify(yieldToWorst),
    JSON.stringify(quantity),
    JSON.stringify(price),
    JSON.stringify(coupon)
  ]);

  // Snapshot of the originally prefilled filter input to compare on Apply
  const originalInputRef = useRef(null);
  // Guard to ensure we capture original snapshot exactly once per load
  const originalCapturedRef = useRef(false);

  // Access characteristic id and reset function from store
  const characteristicId = useBondCharacteristicStore((s) => s.characteristicId);

  const constructFilterInput = (includePagination = false) => {
    // Decide if we should use absolute dates from yieldtable or numeric years
    const hasNumericMin = maturityDates[currentTab].min !== null && maturityDates[currentTab].min !== undefined;
    const hasNumericMax = maturityDates[currentTab].max !== null && maturityDates[currentTab].max !== undefined;
    // Change: use absolute dates whenever we're in yieldtable mode and have preview dates,
    // regardless of numeric prefill presence. User edits flip source off yieldtable elsewhere.
    const useAbsoluteDates = source === 'yieldtable' && (maturityPreviewDates.from || maturityPreviewDates.to);

    return {
      type: currentTab,
      ...(includePagination ? { page: 0, size: 20 } : {}), // Only include pagination if flag is true
      ...(tabValue === 0 && type ? { subType: type } : {}),
      ...(tabValue === 2 && state ? { state } : {}),
      // Only include description for Corp tab
      ...(currentTab === "Corp" && description[currentTab] ? { description: description[currentTab] } : {}),
      // Only include issuer for Corp tab
      ...(currentTab === "Corp" && issuer[currentTab] ? { issuer: issuer[currentTab] } : {}),
      ...(brokerDealer[currentTab] ? { brokerDealer: brokerDealer[currentTab] } : {}), // Use tab-specific broker dealer
      ...(() => {
        const sc = securityCharge[currentTab];
        if (!sc || (Array.isArray(sc) && sc.length === 0)) return {};
        if (Array.isArray(sc)) return { securityCharge: sc.includes("ALL") ? "ALL" : sc };
        return { securityCharge: sc };
      })(),
      // Ratings: if yieldtable with preview, use preview; else map from operator/value
      ...((tabValue === 1 || tabValue === 2) && source === 'yieldtable' && (ratingPreview[currentTab]?.moody?.from || ratingPreview[currentTab]?.moody?.to)
        ? {
            ...(ratingPreview[currentTab].moody.from ? { minRatingMoody: ratingPreview[currentTab].moody.from } : {}),
            ...(ratingPreview[currentTab].moody.to ? { maxRatingMoody: ratingPreview[currentTab].moody.to } : {}),
          }
        : ((tabValue === 1 || tabValue === 2) && moodyRatings[currentTab]
          ? getMinMaxLabels(moodyRatings[currentTab], moodyRatingOptions, "Moody")
          : {})),
      ...((tabValue === 1 || tabValue === 2) && source === 'yieldtable' && (ratingPreview[currentTab]?.sp?.from || ratingPreview[currentTab]?.sp?.to)
        ? {
            ...(ratingPreview[currentTab].sp.from ? { minRatingSP: ratingPreview[currentTab].sp.from } : {}),
            ...(ratingPreview[currentTab].sp.to ? { maxRatingSP: ratingPreview[currentTab].sp.to } : {}),
          }
        : ((tabValue === 1 || tabValue === 2) && spRatings[currentTab]
          ? getMinMaxLabels(spRatings[currentTab], spRatingOptions, "SP")
          : {})),
      // Maturity: use pure dates for yieldtable mode, else append Y
      ...(useAbsoluteDates && maturityPreviewDates.from ? { minMaturity: maturityPreviewDates.from } : {}),
      ...(useAbsoluteDates && maturityPreviewDates.to ? { maxMaturity: maturityPreviewDates.to } : {}),
      ...(!useAbsoluteDates && hasNumericMin ? { minMaturity: `${maturityDates[currentTab].min}Y` } : {}),
      ...(!useAbsoluteDates && hasNumericMax ? { maxMaturity: `${maturityDates[currentTab].max}Y` } : {}),
      ...(yieldToWorst[currentTab].check && yieldToWorst[currentTab].min !== null
        ? { minYtw: yieldToWorst[currentTab].min.toString() }
        : {}),
      ...(yieldToWorst[currentTab].check && yieldToWorst[currentTab].max !== null
        ? { maxYtw: yieldToWorst[currentTab].max.toString() }
        : {}),
      ...(price[currentTab].check && price[currentTab].min !== null
        ? { minPrice: price[currentTab].min.toString() }
        : {}),
      ...(price[currentTab].check && price[currentTab].max !== null
        ? { maxPrice: price[currentTab].max.toString() }
        : {}),
      ...(quantity[currentTab].check && quantity[currentTab].min !== null
        ? { minQty: quantity[currentTab].min.toString() }
        : {}),
      ...(quantity[currentTab].check && quantity[currentTab].max !== null
        ? { maxQty: quantity[currentTab].max.toString() }
        : {}),
      ...(coupon[currentTab].check && coupon[currentTab].min !== null
        ? { minCoupon: coupon[currentTab].min.toString() }
        : {}),
      ...(coupon[currentTab].check && coupon[currentTab].max !== null
        ? { maxCoupon: coupon[currentTab].max.toString() }
        : {}),
      ...(side[currentTab] ? { side: side[currentTab] } : {}),
      ...(callable[currentTab] ? { callable: callable[currentTab] === "1" ? "Yes" : "No" } : {}),
      ...(paymentFrequency ? { paymentFrequency } : {}),
      ...(sector && (currentTab === "Corp" || currentTab === "Muni") ? { sector } : {}),
      ...(nextCallDate && currentTab === "Corp"
        ? { nextCallDate: dayjs(nextCallDate).format("YYYY-MM-DD") }
        : {}),
      ...(taxable ? { taxable: taxable === "1" ? "Yes" : "No" } : {}),
      ...(incomeSource && currentTab === "Muni" ? { incomeSource } : {}),
      ...(altMinTax ? { altMinTax: altMinTax === "1" ? "1" : "0" } : {}),
      // ...(insured ? { insured: insured === "1" ? "1" : "0" } : {}),
    };
  };
  // After loading finishes (prefill complete), capture a normalized snapshot once
  useEffect(() => {
    if (!isLoadingFilter && !originalCapturedRef.current) {
      originalInputRef.current = constructFilterInput(); // normalized snapshot
      originalCapturedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingFilter]);

  // NEW: when user switches tab, reset snapshot so we re-capture for the new tab prefill
  useEffect(() => {
    originalCapturedRef.current = false;
    originalInputRef.current = null;
  }, [tabValue]);

  // Helper to deep-compare objects with sorted keys and without empty fields/source
  const sortAndPrune = (obj) => {
    if (Array.isArray(obj)) return obj.map(sortAndPrune);
    if (obj && typeof obj === "object") {
      const sorted = {};
      Object.keys(obj)
        .sort()
        .forEach((k) => {
          if (k === "source") return; // ignore 'source' for equality
          const v = obj[k];
          // prune empties and normalize strings
          if (v === undefined || v === null) return;
          if (typeof v === "string") {
            const t = v.trim();
            if (t === "") return;
            sorted[k] = t;
          } else {
            sorted[k] = sortAndPrune(v);
          }
        });
      return sorted;
    }
    return obj;
  };
  const deepEqual = (a, b) =>
    JSON.stringify(sortAndPrune(a)) === JSON.stringify(sortAndPrune(b));

  // NEW: safe wrapper to avoid throwing during comparison
  const safeDeepEqual = (a, b) => {
    try {
      return deepEqual(a, b);
    } catch (err) {
      console.error("Filter comparison failed:", err);
      // Fail-safe: treat as unchanged to avoid clearing characteristicId incorrectly
      return true;
    }
  };

  // Inside useFilterPopup hook
  const handleApplyFilter = () => {
    const input = constructFilterInput(); // Don't include pagination when applying

    // If and only if user changed anything since prefill, the search is no longer the
    // characteristic's: drop the characteristic and any ladder/rung linkage from the order
    // too, so an order raised afterwards is a plain order.
    if (characteristicId && originalInputRef.current) {
      const isUnchanged = safeDeepEqual(originalInputRef.current, input);
      if (!isUnchanged) {
        clearCharacteristicLinkage();
      }
    }

    // Preserve yieldtable source if still in that mode
    const outgoingSource = source === 'yieldtable' ? 'yieldtable' : 'filteredSource';
    navigate("/inventory", { 
      state: {
        ...input,
        source: outgoingSource
      }
    });
  };
  const handleSaveFilter = () => {
    if (isEdit && savedFiltersId && editFilterName) {
      handleActualSaveFilter(editFilterName);
    } else {
      setIsNameModalOpen(true);
    }
  };

  const handleActualSaveFilter = async (name) => {
    const filterInput = constructFilterInput(); // Don't include pagination when saving
    const filterData = {
      filterName: name,
      savedFiltersId: isEdit ? savedFiltersId : undefined,
      savedFilterJson: JSON.stringify(filterInput),
    };

    try {
      if (isEdit && editFilterName && savedFiltersId) {
        await updateFilter(token, savedFiltersId, filterData);
        alert("Filter updated successfully!");
      } else {
        await saveFilter(token, filterData);
        alert("Filter saved successfully!");
      }
      setIsNameModalOpen(false);
      setFilterName("");
      navigate("/inventory", { 
        state: {
          criteria: filterInput,
          filterName: name,
          source: 'filteredSource'
        } 
      });
    } catch (error) {
      console.error("Error saving/updating filter:", error);
      alert(error.message || "Error saving filter");
    }
  };

  const handleClearFilter = () => {
    // Reset UI state values
    setType("");
    setState("");
    setSide(prev => ({
      ...prev,
      [currentTab]: "" // Reset to empty string instead of "BUY"
    }));
    setDescription(prev => ({
      ...prev,
      [currentTab]: "" // Reset description for current tab only
    }));
    setIssuer(prev => ({
      ...prev,
      [currentTab]: "" // Reset issuer for current tab only
    }));
    setBrokerDealer(prev => ({
      ...prev,
      [currentTab]: "" // Reset broker dealer for current tab
    }));
    setSecurityCharge(prev => ({
      ...prev,
      [currentTab]: ""
    }));
    setMaturityDates(prev => ({
      ...prev,
      [currentTab]: { min: null, max: null }
    }));
    
    // Reset callable for current tab
    setCallable(prev => ({
      ...prev,
      [currentTab]: ""
    }));
    
    // Reset ratings for Corp and Muni tabs
    if (currentTab === "Corp" || currentTab === "Muni") {
      setMoodyRatings(prev => ({
        ...prev,
        [currentTab]: { operator: "", value: "" }
      }));
      
      setSpRatings(prev => ({
        ...prev,
        [currentTab]: { operator: "", value: "" }
      }));

      // NEW: clear rating previews for current tab
      setRatingPreview(prev => ({
        ...prev,
        [currentTab]: { moody: { from: "", to: "" }, sp: { from: "", to: "" } }
      }));
    }
    
    // Reset other fields
    setPaymentFrequency("");
    setSector("");
    setNextCallDate(null);
    setTaxable("");
    setIncomeSource("");
    setAltMinTax("");
    
    // Fetch initial values for the current tab to get default values
    const payload = { type: currentTab };
    fetchFilterInitialValues(token, payload).then(res => {
      if (res) {
        // Update options
        setSubTypeOptions(res.subType || []);
        setStateOptions(res.state || []);
        setSpRatingOptions(res.spRatingMap || []);
        setMoodyRatingOptions(res.moodyRatingMap || []);
        
        // Set default min-max values from API but keep checkboxes unchecked
        setYieldToWorst((prev) => ({
          ...prev,
          [currentTab]: {
            min: res.minYtw ?? null,
            max: res.maxYtw ?? null,
            check: false, // Keep checkbox unchecked
          },
        }));
        
        setQuantity((prev) => ({
          ...prev,
          [currentTab]: {
            min: res.minQty ?? null,
            max: res.maxQty ?? null,
            check: false, // Keep checkbox unchecked
          },
        }));
        
        setPrice((prev) => ({
          ...prev,
          [currentTab]: {
            min: res.minPrice ?? null,
            max: res.maxPrice ?? null,
            check: false, // Keep checkbox unchecked
          },
        }));
        
        setCoupon((prev) => ({
          ...prev,
          [currentTab]: {
            min: res.minCoupon ?? null,
            max: res.maxCoupon ?? null,
            check: false, // Keep checkbox unchecked
          },
        }));
      }
    });
  };

  // Update source when user edits maturity after yieldtable prefill
  useEffect(() => {
    if (maturityEditedByUser && source === 'yieldtable' && !isLoadingFilter) {
      setSource('filteredSource');
      setMaturityPreviewDates({ from: null, to: null });
      resetMaturityEditedByUser();
    }
  }, [maturityEditedByUser, source, isLoadingFilter, resetMaturityEditedByUser]);

  // NEW: Update source when user edits ratings after yieldtable prefill
  useEffect(() => {
    if (source !== 'yieldtable' || isLoadingFilter) return;
    const mr = moodyRatings[currentTab] || {};
    const sr = spRatings[currentTab] || {};
    const moodyEdited = (mr.operator && mr.operator.trim() !== "") || (mr.value && mr.value !== "");
    const spEdited = (sr.operator && sr.operator.trim() !== "") || (sr.value && sr.value !== "");
    if (moodyEdited || spEdited) {
      setSource('filteredSource');
      // clear rating preview for this tab
      setRatingPreview(prev => ({
        ...prev,
        [currentTab]: { moody: { from: "", to: "" }, sp: { from: "", to: "" } }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(moodyRatings), JSON.stringify(spRatings), currentTab, source, isLoadingFilter]);

  const loadFilterData = async (filterData, id) => {
    setIsLoadingFilter(true);
    // Reset original snapshot capture for this new load
    originalCapturedRef.current = false;
    originalInputRef.current = null;

    setSavedFiltersId(id);
    setIsEdit(true);
    // Ensure edit flag is clean before applying prefill values
    resetMaturityEditedByUser();
    const tabType = filterData.type || "Gov";
    const tabMapping = { Gov: 0, Corp: 1, Muni: 2 };
    setTabValue(tabMapping[tabType]);

    // Fetch options only
    const payload = { type: tabType };
    let res;
    res = await fetchFilterInitialValues(token, payload);

    if (res) {
      // Filter out null/empty options when loading filter data
      setSubTypeOptions((res.subType || []).filter(option => option && option.trim() !== ""));
      setStateOptions((res.state || []).filter(option => 
        option && 
        option.value && 
        option.value.trim() !== "" && 
        option.name && 
        option.name.trim() !== ""
      ));
      setSpRatingOptions((res.spRatingMap || []).filter(option => 
        option && 
        option.rating && 
        option.rating.trim() !== "" && 
        option.value !== null && 
        option.value !== undefined
      ));
      setMoodyRatingOptions((res.moodyRatingMap || []).filter(option => 
        option && 
        option.rating && 
        option.rating.trim() !== "" && 
        option.value !== null && 
        option.value !== undefined
      ));
    }

    // Apply saved filter data
    if (filterData.subType) setType(filterData.subType);
    if (filterData.state) setState(filterData.state);
    if (filterData.side) {
      setSide(prev => ({
        ...prev,
        [tabType]: filterData.side
      }));
    }
    if (filterData.description && tabType === "Corp") {
      setDescription(prev => ({
        ...prev,
        Corp: filterData.description // Only load description for Corp tab
      }));
    }
    if (filterData.issuer && tabType === "Corp") {
      setIssuer(prev => ({
        ...prev,
        Corp: filterData.issuer // Only load issuer for Corp tab
      }));
    }
    if (filterData.brokerDealer) {
      setBrokerDealer(prev => ({
        ...prev,
        [tabType]: filterData.brokerDealer // Load saved broker dealer for specific tab
      }));
    }
    if (filterData.securityCharge) {
      const sc = filterData.securityCharge;
      setSecurityCharge(prev => ({
        ...prev,
        [tabType]: Array.isArray(sc) ? sc : [sc],
      }));
    }
    if (filterData.callable) {
      setCallable(prev => ({
        ...prev,
        [tabType]: filterData.callable === "Yes" ? "1" : "0"
      }));
    }
    if (filterData.paymentFrequency) setPaymentFrequency(filterData.paymentFrequency);
    if (filterData.sector) setSector(filterData.sector);
    if (filterData.nextCallDate) setNextCallDate(dayjs(filterData.nextCallDate).isValid() ? dayjs(filterData.nextCallDate) : null);
    if (filterData.taxable) setTaxable(filterData.taxable === "Yes" ? "1" : "0");
    if (filterData.incomeSource) setIncomeSource(filterData.incomeSource);
    if (filterData.altMinTax) setAltMinTax(filterData.altMinTax === "1" ? "1" : "0");
    
    // Source and maturity handling
    setSource(filterData.source || "");
    if (filterData.source === 'yieldtable') {
      setMaturityPreviewDates({
        from: filterData.minMaturity || null,
        to: filterData.maxMaturity || null
      });
    }
    // Change: Always prefill numeric years for UI fields (even in yieldtable mode)
    if (filterData.minMaturity) {
      const minYears = convertMaturityToYears(filterData.minMaturity);
      setMaturityDates(prev => ({
        ...prev,
        [tabType]: { ...prev[tabType], min: minYears }
      }));
    }
    if (filterData.maxMaturity) {
      const maxYears = convertMaturityToYears(filterData.maxMaturity);
      setMaturityDates(prev => ({
        ...prev,
        [tabType]: { ...prev[tabType], max: maxYears }
      }));
    }

    const minMaxFields = ["Ytw", "Price", "Qty", "Coupon"];
    minMaxFields.forEach((field) => {
      const minKey = `min${field}`;
      const maxKey = `max${field}`;
      if (filterData[minKey] !== undefined || filterData[maxKey] !== undefined) {
        const setter = { Ytw: setYieldToWorst, Price: setPrice, Qty: setQuantity, Coupon: setCoupon }[field];
        setter((prev) => ({
          ...prev,
          [tabType]: {
            min: filterData[minKey] ? parseFloat(filterData[minKey]) : null,
            max: filterData[maxKey] ? parseFloat(filterData[maxKey]) : null,
            check: filterData[minKey] !== undefined || filterData[maxKey] !== undefined,
          },
        }));
      }
    });

    // Handle Moody's Ratings and S&P Ratings
    // NEW: If yieldtable source, don't prefill selects; instead, store preview ranges
    if (filterData.source === 'yieldtable') {
      setRatingPreview(prev => ({
        ...prev,
        [tabType]: {
          moody: {
            from: filterData.minRatingMoody || "",
            to: filterData.maxRatingMoody || ""
          },
          sp: {
            from: filterData.minRatingSP || "",
            to: filterData.maxRatingSP || ""
          }
        }
      }));
    } else {
      // existing behavior: prefill operator/value inferred from min/max
      if (filterData.minRatingMoody || filterData.maxRatingMoody) {
        const moodyRating = determineOperatorAndValue(
          filterData.minRatingMoody,
          filterData.maxRatingMoody,
          res.moodyRatingMap || []
        );
        setMoodyRatings(prev => ({
          ...prev,
          [tabType]: moodyRating
        }));
      }
      if (filterData.minRatingSP || filterData.maxRatingSP) {
        const spRating = determineOperatorAndValue(
          filterData.minRatingSP,
          filterData.maxRatingSP,
          res.spRatingMap || []
        );
        setSpRatings(prev => ({
          ...prev,
          [tabType]: spRating
        }));
      }
      // also clear any stale preview for this tab
      setRatingPreview(prev => ({
        ...prev,
        [tabType]: { moody: { from: "", to: "" }, sp: { from: "", to: "" } }
      }));
    }

    setIsLoadingFilter(false);
  };

  const determineOperatorAndValue = (minRating, maxRating, ratingOptions) => {
    if (!ratingOptions.length || (!minRating && !maxRating)) {
      return { operator: "", value: "" };
    }
  
    const lowestRating = ratingOptions[ratingOptions.length - 1].rating;
    const highestRating = ratingOptions[0].rating;
  
    // Case 1: Only min rating is set (greater than)
    if (minRating && (!maxRating || maxRating === highestRating)) {
      const option = ratingOptions.find(opt => opt.rating === minRating);
      if (option) {
        return { operator: "gt", value: option.value };
      }
    }
  
    // Case 2: Only max rating is set (less than)
    if (maxRating && (!minRating || minRating === lowestRating)) {
      const option = ratingOptions.find(opt => opt.rating === maxRating);
      if (option) {
        return { operator: "lt", value: option.value };
      }
    }
  
    // Default case: no valid operator found
    return { operator: "", value: "" };
  };
 
  const enableTabSwitching = () => {
    setTabSwitchingEnabled(true);
    setPreserveFilterData(true); // Set flag to preserve filter data
    setIsEdit(false);
    setEditFilterName("");
    setSavedFiltersId(null);
  };

  // Modify exposed methods to directly update state without redundant debouncing
  return {
    tabValue,
    setTabValue,
    criteriaCount,
    isCriteriaLoading,
    type,
    setType,
    state,
    setState,
    description: description[currentTab],
    setDescription: (value) => {
      // Just update state directly - the component already has debounce
      setDescription(prev => ({
        ...prev,
        [currentTab]: value
      }));
    },
    issuer: issuer[currentTab],
    setIssuer: (value) => {
      // Just update state directly - the component already has debounce
      setIssuer(prev => ({
        ...prev,
        [currentTab]: value
      }));
    },
    issuerOptions,
    issuerValidationError: issuerValidationError[currentTab],
    brokerDealer: brokerDealer[currentTab],
    setBrokerDealer: (value) => setBrokerDealer(prev => ({
      ...prev,
      [currentTab]: value
    })),
    brokerDealerOptions,
    securityCharge: securityCharge[currentTab],
    setSecurityCharge: (value) => setSecurityCharge(prev => ({
      ...prev,
      [currentTab]: value
    })),
    securityChargeOptions,
    maturityMinDate: maturityDates[currentTab].min,
    maturityMaxDate: maturityDates[currentTab].max,
    setMaturityMinDate: (value) => {
      setMaturityDates(prev => ({
        ...prev,
        [currentTab]: { ...prev[currentTab], min: value }
      }));
    },
    setMaturityMaxDate: (value) => {
      setMaturityDates(prev => ({
        ...prev,
        [currentTab]: { ...prev[currentTab], max: value }
      }));
    },
    // Provide preview dates and validation flag to UI
    maturityPreviewMinDate: maturityPreviewDates.from,
    maturityPreviewMaxDate: maturityPreviewDates.to,
    skipMaturityValidation: source === 'yieldtable' && (maturityPreviewDates.from || maturityPreviewDates.to),
    side: side[currentTab],
    setSide: (value) => setSide(prev => ({
      ...prev,
      [currentTab]: value
    })),
    callable: callable[currentTab],
    setCallable: (value) => setCallable(prev => ({
      ...prev,
      [currentTab]: value
    })),
    paymentFrequency,
    setPaymentFrequency,
    sector,
    setSector,
    nextCallDate,
    setNextCallDate,
    taxable,
    setTaxable,
    incomeSource,
    setIncomeSource,
    altMinTax,
    setAltMinTax,
    yieldToWorst,
    setYieldToWorst,
    quantity,
    setQuantity,
    price,
    setPrice,
    coupon,
    setCoupon,
    moodyRatings,
    setMoodyRatings,
    spRatings,
    setSpRatings,
    subTypeOptions,
    stateOptions,
    moodyRatingOptions,
    spRatingOptions,
    paymentFrequencyOptions,
    sectorOptions,
    incomeSourceOptions,
    handleApplyFilter,
    handleClearFilter,
    handleSaveFilter,
    currentTab,
    isNameModalOpen,
    setIsNameModalOpen,
    filterName,
    setFilterName,
    handleActualSaveFilter,
    isEdit,
    setIsEdit,
    editFilterName,
    setEditFilterName,
    loadFilterData,
    savedFiltersId,
    setSavedFiltersId,
    tabSwitchingEnabled,
    enableTabSwitching,
    // New: expose whether current source is yieldtable (drives UI tag)
    isYieldTableSource: source === 'yieldtable',
    // NEW: expose rating previews for current tab
    moodyPreviewFrom: ratingPreview[currentTab]?.moody?.from || "",
    moodyPreviewTo: ratingPreview[currentTab]?.moody?.to || "",
    spPreviewFrom: ratingPreview[currentTab]?.sp?.from || "",
    spPreviewTo: ratingPreview[currentTab]?.sp?.to || "",
  };
}
